import type { EventRepository, CreateEventInput, UpdateEventInput } from "@/data/repositories/eventRepository";
import type { Event } from "@/types/domain/event";
import { DUMMY_EVENTS } from "@/data/dummy/dummyEventData";

/**
 * In-memory implementation of EventRepository. Holds its own mutable copy
 * of the seed data (never mutates the shared DUMMY_EVENTS array) so
 * create/update calls persist for the lifetime of the session, the same
 * way the old inline `useState(DUMMY_WEDDINGS)` did in WeddingContext —
 * just behind the repository interface now instead of a direct import.
 */
export class DummyEventRepository implements EventRepository {
  private static readonly STORAGE_KEY = "atelier.demo-events-v1";
  private events: Event[];

  constructor() {
    this.events = this.load();
  }

  private load(): Event[] {
    if (typeof window === "undefined") return DUMMY_EVENTS.map((event) => ({ ...event }));
    const stored = window.localStorage.getItem(DummyEventRepository.STORAGE_KEY);
    if (stored === null) return DUMMY_EVENTS.map((event) => ({ ...event }));
    try {
      const parsed: unknown = JSON.parse(stored);
      return Array.isArray(parsed) ? (parsed as Event[]) : DUMMY_EVENTS.map((event) => ({ ...event }));
    } catch {
      return DUMMY_EVENTS.map((event) => ({ ...event }));
    }
  }

  private persist() {
    if (typeof window !== "undefined") window.localStorage.setItem(DummyEventRepository.STORAGE_KEY, JSON.stringify(this.events));
  }

  async listByOrganization(organizationId: string): Promise<Event[]> {
    return this.events.filter((e) => e.organizationId === organizationId);
  }

  async getById(id: string): Promise<Event | null> {
    return this.events.find((e) => e.id === id) ?? null;
  }

  async create(input: CreateEventInput): Promise<Event> {
    const now = new Date().toISOString();
    const { weddingDetails, ...eventInput } = input;
    const id = `evt_${crypto.randomUUID()}`;
    const event: Event = {
      id,
      createdAt: now,
      updatedAt: now,
      ...eventInput,
      weddingDetails: weddingDetails ? {
        eventId: id,
        partnerOneName: weddingDetails.partnerOneName ?? "",
        partnerTwoName: weddingDetails.partnerTwoName ?? "",
        createdAt: now,
        updatedAt: now,
        ...weddingDetails,
      } : undefined,
    };
    this.events.push(event);
    this.persist();
    return event;
  }

  async update(id: string, patch: UpdateEventInput): Promise<Event> {
    const index = this.events.findIndex((e) => e.id === id);
    const existing = this.events[index];
    if (!existing) throw new Error(`Event ${id} not found`);
    const { weddingDetails, suspendedAt, deletedAt, purgeAfter, ...eventPatch } = patch;
    const now = new Date().toISOString();
    const updated: Event = {
      ...existing,
      ...eventPatch,
      ...(suspendedAt !== undefined && { suspendedAt: suspendedAt ?? undefined }),
      ...(deletedAt !== undefined && { deletedAt: deletedAt ?? undefined }),
      ...(purgeAfter !== undefined && { purgeAfter: purgeAfter ?? undefined }),
      updatedAt: now,
      weddingDetails: weddingDetails ? {
        eventId: id,
        partnerOneName: weddingDetails.partnerOneName ?? existing.weddingDetails?.partnerOneName ?? "",
        partnerTwoName: weddingDetails.partnerTwoName ?? existing.weddingDetails?.partnerTwoName ?? "",
        createdAt: existing.weddingDetails?.createdAt ?? now,
        ...existing.weddingDetails,
        ...weddingDetails,
        updatedAt: now,
      } : existing.weddingDetails,
    };
    this.events[index] = updated;
    this.persist();
    return updated;
  }
}
